import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettings } from '../settings/app-settings';

export interface RmsCartItem {
    supplierCode: string;
    productCode: string;
    quantity: number;
    barcode: string;
    sellingPrice: number;
}

export interface RmsIdentifyRequest {
    /**
     * LP-5200: the redemption credential, transport-neutral and deliberately NOT named `qr`.
     * LovCash does not care whether it was scanned as a QR, read by another supported scanner, or
     * typed by hand; it canonicalizes and validates it server-side. The backend record carries no
     * `qr` alias, and because its DTO ignores unknown properties, a request still sending `qr`
     * is accepted and then silently treated as supplying NO credential at all.
     */
    code?: string;

    mobileNumber?: string;

    /**
     * LP-5140: idempotency key for the `mobileNumber` (cashier capture) path.
     *
     * <p>Required there and rejected if blank or over 191 characters; ignored on the `code` path,
     * which has its own duplicate-guess protection (LP-5244). Sent on both paths here because the
     * backend ignores it where it does not apply, which keeps this client free of a branch the
     * contract does not require.
     *
     * <p>Reusing a value replays the first outcome for that wholesaler rather than identifying
     * again, so a till retrying one logical capture cannot cause a second signup or a second
     * session token.
     */
    requestId?: string;
}

/** LP-5197: cashier display/visual-confirmation only - never an input to any later RMS call. */
export interface RmsTraderDisplay {
    displayName: string;
    mobileNumberMasked: string;
}

export interface RmsIdentifyResponse {
    status: string;
    trader: RmsTraderDisplay;
    token: string;
    expiresAt: string;
}

export interface RmsCheckoutRequest {
    requestId: string;
    token: string;
    checkoutTillId: string;
    basketTotal: number;
    cart: RmsCartItem[];
}

export interface RmsCheckoutItemResponse {
    [key: string]: any;
}

export interface RmsCheckoutResponse {
    checkoutId: string;
    status: string;
    redemptionAmount: number;
    authCode: string;
    items: RmsCheckoutItemResponse[];
}

export interface RmsSaleConfirmationRequest {
    saleReference: string;
    checkoutId: string;
    // LP-5198: checkout-scoped confirmation credential returned by Checkout's own authCode field
    // - required from this point on; a missing/wrong value is rejected before any durable
    // confirmation happens (400 VALIDATION_FAILED / 422 AUTH_CODE_INVALID respectively).
    authCode: string;
    saleConfirmationTillId: string;
    completedAt: string;
    finalTotal: number;
}

export interface RmsSaleConfirmationResponse {
    status: string;
    checkoutId: string;
    saleReference: string;
    errors: string[];
}

/**
 * Calls the real external `external/wholesaler/rms/v1/**` contract exactly as a physical till
 * would - same endpoints, same request/response shapes, same RMS-X-AUTH-KEY wholesaler
 * authentication. Used by the RMS Simulator admin page to demo/exercise the RMS flow end to end
 * without needing real hardware.
 *
 * <p>RMS-X-AUTH-KEY is deliberately per-call, supplied by the caller rather than read from any
 * stored/global auth state - it authenticates as a WHOLESALER (see
 * AppSettings.CREATE_ROTATE_WHOLESALER_API_KEY, where that key is issued/rotated), a completely
 * different identity from the portal's own staff session, which HttpRequestInterceptor already
 * attaches to every request via its own X-Auth-Key header. Both headers can coexist on the same
 * request; the backend's RmsAuthInterceptor only ever looks at RMS-X-AUTH-KEY.
 */
@Injectable({ providedIn: 'root' })
export class RmsSimulatorService {
    constructor(private http: HttpClient) {}

    private headersFor(rmsAuthKey: string): HttpHeaders {
        return new HttpHeaders({
            'RMS-X-AUTH-KEY': rmsAuthKey,
            // A wrong/expired/rotated RMS key is a normal outcome here, shown inline - it must
            // never be mistaken by HttpRequestInterceptor for the STAFF session being invalid
            // and log the operator out of the whole portal mid-demo. See its own comment on
            // SKIP_AUTH_LOGOUT_HEADER.
            'X-Skip-Auth-Logout': 'true',
        });
    }

    identify(rmsAuthKey: string, body: RmsIdentifyRequest) {
        return this.http.post<RmsIdentifyResponse>(
            AppSettings.API_ENDPOINT + AppSettings.RMS_IDENTIFY,
            body,
            { headers: this.headersFor(rmsAuthKey) },
        );
    }

    checkout(rmsAuthKey: string, body: RmsCheckoutRequest) {
        return this.http.post<RmsCheckoutResponse>(
            AppSettings.API_ENDPOINT + AppSettings.RMS_CHECKOUT,
            body,
            { headers: this.headersFor(rmsAuthKey) },
        );
    }

    confirmSale(rmsAuthKey: string, body: RmsSaleConfirmationRequest) {
        return this.http.post<RmsSaleConfirmationResponse>(
            AppSettings.API_ENDPOINT + AppSettings.RMS_SALES_CONFIRM,
            body,
            { headers: this.headersFor(rmsAuthKey) },
        );
    }
}
